import React, { useEffect, useMemo, useState } from "react";
import Icon from "../shared/Icon";
import { connect } from "react-redux";
import { compose } from "redux";
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";
import {
  fetchAllOrders,
  fetchResidents,
  isAbortError,
  RESIDENT_CREATE_ENABLED,
} from "../../../services/managerApi";
import { residentPath } from "../../../utils/managerRouting";
import WoshId from "../shared/WoshId";
import AddResidentForm from "./AddResidentForm";
import useManagerScope from "../../../hooks/useManagerScope";
import { EmptyState, ErrorState, LoadingRows } from "../shared/States";
import { formatCurrency, formatKilos, formatShortDate } from "../shared/format";

const GRID = "1.1fr 2fr 1.2fr 1fr .9fr 1.2fr";

/**
 * Fusionne l'annuaire et les commandes.
 *
 * L'annuaire donne QUI existe — y compris un résident sans aucune commande,
 * donc celui qu'on vient de créer. Les commandes donnent l'activité : nombre,
 * kilos, total, dernier enlèvement. Aucune des deux sources ne suffit seule.
 *
 * L'annuaire n'est pas obligatoire : s'il est vide ou indisponible, la liste
 * reste celle déduite des commandes, exactement comme avant sa livraison.
 */
export const groupResidents = (orders, directory = []) => {
  const map = new Map();
  // Pont entre les deux sources : `/customer/managers` ne sérialise pas
  // `user_id`, mais la référence de commande donne l'identifiant WOSH, que
  // l'annuaire porte aussi. Sans ce pont, le même résident entrait deux fois —
  // une fois par l'annuaire sous son id numérique, une fois par ses commandes
  // sous son nom.
  const byWoshId = new Map();

  directory.forEach((resident) => {
    const key = resident.id ?? resident.woshUserId;
    if (key == null) return;
    if (resident.woshUserId) byWoshId.set(String(resident.woshUserId), key);
    map.set(key, {
      id: resident.id ?? null,
      key: resident.key ?? key,
      woshUserId: resident.woshUserId ?? null,
      name: resident.name,
      room: null,
      orders: 0,
      kilos: 0,
      total: 0,
      lastOrder: null,
    });
  });

  orders.forEach((order) => {
    const fromDirectory =
      (order.residentId != null && map.has(order.residentId) ? order.residentId : null) ||
      (order.woshUserId ? byWoshId.get(String(order.woshUserId)) : null);

    const key = fromDirectory ?? order.residentId ?? order.woshUserId ?? order.residentName;
    if (!key) return;

    const entry = map.get(key) || {
      id: order.residentId ?? null,
      // Clé de navigation : `user_id` si l'API le donne, sinon l'identifiant
      // WOSH. Sans repli, une API qui ne sérialise pas `user_id` rendrait
      // toute la liste non cliquable, sans le moindre signe à l'écran.
      key: order.residentId ?? order.woshUserId ?? null,
      woshUserId: order.woshUserId ?? null,
      name: order.residentName,
      room: order.room,
      orders: 0,
      kilos: 0,
      total: 0,
      lastOrder: null,
    };

    // Toutes les commandes d'un résident ne portent pas forcément une
    // référence exploitable : on retient la première qui en donne une.
    if (!entry.woshUserId && order.woshUserId) entry.woshUserId = order.woshUserId;
    // L'identifiant numérique de l'annuaire prime : c'est celui que le serveur
    // attend pour toute écriture.
    if (entry.id == null && order.residentId != null) entry.id = order.residentId;
    if (!entry.name && order.residentName) entry.name = order.residentName;
    if (entry.room == null && order.room != null) entry.room = order.room;

    entry.orders += 1;
    entry.kilos += order.kilos || 0;
    entry.total += order.total || 0;
    if (!entry.lastOrder || (order.pickupDate && order.pickupDate > entry.lastOrder)) {
      entry.lastOrder = order.pickupDate;
    }

    map.set(key, entry);
  });

  return [...map.values()].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
};

const Bewoners = ({ t, authUser, history }) => {
  const [orders, setOrders] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [isAdding, setIsAdding] = useState(false);
  const { collectionPoints } = useManagerScope(authUser);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        // L'annuaire ne doit pas faire tomber la page : livré récemment, il
        // peut manquer d'un environnement. Son échec laisse la liste déduite
        // des commandes, qui est le comportement d'avant.
        const [result, roster] = await Promise.all([
          fetchAllOrders({ authUser, signal: controller.signal }),
          fetchResidents({ authUser, signal: controller.signal }).catch(() => []),
        ]);
        if (!active) return;
        setOrders(result.orders);
        setDirectory(roster);
      } catch (err) {
        if (!active || isAbortError(err)) return;
        setError(err);
      } finally {
        if (active) setIsLoading(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [authUser, reloadKey]);

  const residents = useMemo(() => groupResidents(orders, directory), [orders, directory]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return residents;
    return residents.filter(
      (resident) =>
        (resident.name || "").toLowerCase().includes(term) ||
        String(resident.woshUserId || "").toLowerCase().includes(term) ||
        String(resident.room || "").toLowerCase().includes(term)
    );
  }, [residents, search]);

  return (
    <>
      <div className="mgr-toolbar">
        <h1 className="mgr-h1">{t("Bewoners")}</h1>
        <div className="mgr-search-sm">
          <Icon name="search" style={{ fontSize: 19, color: "var(--mgr-brand)" }} />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("Zoek bewoner of gebruikers-ID")}
            aria-label={t("Zoek bewoner of gebruikers-ID")}
          />
        </div>
        {/* Le point d'entrée de la création est masqué tant que le serveur
            n'expose pas le périmètre du manager : sans identifiant
            d'établissement à envoyer, le POST repart en 422. Un bouton qui
            mène à une erreur systématique vaut moins que pas de bouton. */}
        {RESIDENT_CREATE_ENABLED ? (
          <button
            type="button"
            className="mgr-btn-primary"
            aria-expanded={isAdding}
            onClick={() => setIsAdding((open) => !open)}
          >
            <Icon name="add" style={{ fontSize: 19 }} />
            {t("Bewoner toevoegen")}
          </button>
        ) : null}
      </div>

      {RESIDENT_CREATE_ENABLED && isAdding ? (
        <AddResidentForm
          authUser={authUser}
          collectionPoints={collectionPoints}
          residents={directory}
          onCancel={() => setIsAdding(false)}
          onCreated={() => {
            setIsAdding(false);
            // L'annuaire renvoie le résident dès sa création, sans attendre sa
            // première commande : le rechargement le fait apparaître aussitôt.
            setReloadKey((key) => key + 1);
          }}
          t={t}
        />
      ) : null}

      {error ? (
        <ErrorState
          message={t("De gegevens konden niet geladen worden.")}
          retryLabel={t("Opnieuw proberen")}
          onRetry={() => setReloadKey((key) => key + 1)}
        />
      ) : null}

      <div className="mgr-table">
        <div className="mgr-thead" style={{ gridTemplateColumns: GRID }}>
          <div>{t("WOSH-nummer")}</div>
          <div>{t("Bewoner")}</div>
          <div>{t("Laatste ophaling")}</div>
          <div className="mgr-num">{t("Bestellingen")}</div>
          <div className="mgr-num">{t("Kg")}</div>
          <div className="mgr-num">{t("Totaal")}</div>
        </div>

        {isLoading ? <LoadingRows rows={8} /> : null}

        {!isLoading && visible.length === 0 ? (
          <EmptyState
            title={t("Geen bewoners gevonden")}
            hint={t("Bewoners verschijnen zodra ze een bestelling hebben.")}
          />
        ) : null}

        {!isLoading &&
          visible.map((resident) => (
            <div
              key={resident.id ?? resident.name}
              role="button"
              tabIndex={0}
              className="mgr-tr"
              style={{ gridTemplateColumns: GRID, borderBottom: "1px solid var(--mgr-hairline)" }}
              onClick={() => resident.key && history.push(residentPath(resident.key))}
              onKeyDown={(event) => {
                if ((event.key === "Enter" || event.key === " ") && resident.key) {
                  event.preventDefault();
                  history.push(residentPath(resident.key));
                }
              }}
            >
              <div>
                <WoshId value={resident.woshUserId} t={t} />
              </div>
              <div style={{ fontWeight: 700 }}>
                {resident.name}
                {resident.room ? (
                  <span style={{ fontWeight: 600, color: "var(--mgr-ink-3)" }}>
                    {" "}
                    · {t("k.")} {resident.room}
                  </span>
                ) : null}
              </div>
              <div style={{ fontWeight: 600 }}>{formatShortDate(resident.lastOrder)}</div>
              <div className="mgr-num" style={{ fontWeight: 700 }}>
                {resident.orders}
              </div>
              <div className="mgr-num" style={{ fontWeight: 700 }}>
                {resident.kilos ? formatKilos(resident.kilos) : "—"}
              </div>
              <div className="mgr-num" style={{ fontWeight: 800 }}>
                {formatCurrency(resident.total)}
              </div>
            </div>
          ))}
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  authUser: state?.auth?.user,
});

export default compose(
  withRouter,
  withTranslation(),
  connect(mapStateToProps)
)(Bewoners);
